/** Room the pinned columns always leave for the scrollable ones, roughly two KPI columns. */
export const MIN_SCROLLABLE_WIDTH_PX = 320;

interface ResolvePinnedColumnsInput<ColumnId extends string> {
  columnsOrder: ColumnId[];
  pinnableColumns: ColumnId[];
  columnWidths: Partial<Record<ColumnId, number>>;
  viewportWidth: number;
}

interface PinnedColumnsLayout<ColumnId extends string> {
  pinnedColumns: ColumnId[];
  leftPositions: Partial<Record<ColumnId, number>>;
}

/**
 * Pins the leading pinnable columns as long as they leave a scrollable lane in the viewport.
 * Sticky columns wider than the viewport overlap each other, so pinning stops at the first one that does not fit.
 */
export function resolvePinnedColumns<ColumnId extends string>({
  columnsOrder,
  pinnableColumns,
  columnWidths,
  viewportWidth
}: ResolvePinnedColumnsInput<ColumnId>): PinnedColumnsLayout<ColumnId> {
  const isViewportMeasured = viewportWidth > 0;
  const pinnedWidthBudget = isViewportMeasured ? viewportWidth - MIN_SCROLLABLE_WIDTH_PX : Number.POSITIVE_INFINITY;

  const pinnedColumns: ColumnId[] = [];
  const leftPositions: Partial<Record<ColumnId, number>> = {};
  let pinnedWidth = 0;

  for (const columnId of columnsOrder) {
    const columnWidth = columnWidths[columnId] ?? 0;
    const fitsNextToScrollableLane = pinnedWidth + columnWidth <= pinnedWidthBudget;

    if (!pinnableColumns.includes(columnId) || !fitsNextToScrollableLane) {
      break;
    }

    pinnedColumns.push(columnId);
    leftPositions[columnId] = pinnedWidth;
    pinnedWidth += columnWidth;
  }

  return { pinnedColumns, leftPositions };
}
